import React, { useState, useEffect, InputHTMLAttributes } from "react";
import clsx from "clsx";
import { FiArrowLeft, FiCheck, FiEye, FiEyeOff } from "react-icons/fi";
import { useMutation } from "@tanstack/react-query";
import { useAltcha } from "components/core/Altcha/AltchaWidget";
import { SendOtpApi, ReSendOtpApi, VerifyOtpApi } from "./JoinNowForm.api";
import { toastrSuccess, toastrError } from "./../../lib/toastr";

interface FloatingLabelInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  isEmailVerified?: (verified: boolean) => void;
}

export const FloatingLabelInput: React.FC<FloatingLabelInputProps> = ({
  label,
  type = "text",
  className,
  value: propValue,
  onChange,
  isEmailVerified,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [internalValue, setInternalValue] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [otpMode, setOtpMode] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [verified, setVerified] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [otpVarifyLoading, setOtpVarifyLoading] = useState(false);

  const value = otpMode ? internalValue : propValue ?? internalValue;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInternalValue(e.target.value);
    onChange?.(e);
  };
  const inputType = type === "password" ? (showPassword ? "text" : "password") : type;

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (otpMode && countdown > 0) {
      timer = setTimeout(() => setCountdown((prev) => prev - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [otpMode, countdown]);

  useEffect(() => {
    if (verified) {
      isEmailVerified?.(true);
    }
  }, [verified]);

  const validateEmailFormat = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const { widget: altchaWidget, solve: solveAltcha } = useAltcha("altcha");

  // A fresh challenge is solved for each OTP request. Requesting an OTP is the
  // cheapest way to make us send mail on demand, so it is captcha-gated even
  // though it is not a form submit -- which is why the widget is driven
  // programmatically here rather than via auto="onsubmit".
  const sendOtpMutation = useMutation((altcha: string) => SendOtpApi(value, altcha), {
    onSuccess: (response) => {
      if (response.success) {
        toastrSuccess(response.message || "OTP sent successfully");
        setOtpMode(true);
        setVerifyLoading(false);
      } else {
        toastrError(response.message || "OTP sending failed");
        setVerifyLoading(false);
      }
    },
    onError: () => { toastrError("Failed to send OTP"), setVerifyLoading(false); }
  });

  const resendOtpMutation = useMutation(() => ReSendOtpApi(emailValue), {
    onSuccess: (response) => {
      if (response.success) toastrSuccess(response.message || "OTP resent successfully");
      else toastrError(response.message || "Failed to resend OTP");
    },
    onError: () => toastrError("Failed to resend OTP"),
  });

  const verifyOtpMutation = useMutation(() => VerifyOtpApi(emailValue, value), {
    onSuccess: (response) => {
      if (response.success) {
        toastrSuccess(response.message || "Email verified successfully");
        setVerified(true);
        setOtpMode(false);
        setInternalValue(emailValue);
        setOtpVarifyLoading(false);
      } else {
        toastrError(response.message || "OTP verification failed");
        setOtpVarifyLoading(false);
      }
    },
    onError: () => {toastrError("Failed to verify OTP"); setOtpVarifyLoading(false); },
  });

  const handleVerifyClick = async () => {
    if (!value) {
      toastrError("Email is required");
      return;
    }
    if (!validateEmailFormat(String(value))) {
      toastrError("Invalid email format");
      return;
    }
    setEmailValue(String(value));
    setInternalValue("");
    // setOtpMode(true);
    setCountdown(30);
    setVerifyLoading(true);

    const altcha = await solveAltcha();
    if (!altcha) {
      toastrError("Captcha verification failed. Please try again.");
      setVerifyLoading(false);
      return;
    }
    sendOtpMutation.mutate(altcha);
  };

  const handleResendOtp = () => {
    setCountdown(30);
    resendOtpMutation.mutate();
  };

  const handleVerifyOtp = () => {
    if (String(internalValue).length !== 6) {
      toastrError("OTP should be 6 digits");
      setOtpVarifyLoading(false);
      return;
    }
    setOtpVarifyLoading(true);
    verifyOtpMutation.mutate(undefined, {
      onSuccess: (response) => {
        if (response.success) {
          // toastrSuccess(response.message || "Email verified successfully");
          setVerified(true);
          setOtpMode(false);

          // ✅ parent me email set karo
          onChange?.({
            target: { value: emailValue, name: props.name }
          } as React.ChangeEvent<HTMLInputElement>);
          setInternalValue("");
        } else {
          // toastrError(response.message || "OTP verification failed");
        }
      },
      onError: () => toastrError("Failed to verify OTP"),
      
    });
  };


  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === "email" || type === "password") {
      if (e.key === " ") {
        e.preventDefault();
      }
    }

    if (props.name === "name") {
      if (e.key === " " && !(String(value).length)) {
        e.preventDefault();
      }
    }
  };


  return (
    <div className="relative w-full flex items-center">
      {/* Must be mounted for solve() to have an element to drive. Rendered
          invisible so the email field's layout is unchanged; the work still
          happens in-browser and the OTP button shows the pending state. */}
      {type === "email" ? <span className="hidden">{altchaWidget}</span> : null}
      <div className="relative w-full">
        <input
          type={otpMode ? "text" : inputType}
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          readOnly={verified && type === "email"}
          autoComplete="new-password"
          className={clsx(
            "peer block w-full h-[52px] rounded border border-line-strong bg-surface pl-3.5 pt-5 pb-1.5 text-sm text-ink shadow-xs outline-none transition duration-150 hover:border-ink-4 focus:border-primary focus:ring-[3px] focus:ring-primary/15 read-only:bg-surface-2 read-only:text-ink-2",
            type === "email" && (otpMode || verified || validateEmailFormat(String(value))) ? "pr-28" : "pr-12",
            otpMode && "tracking-[0.3em] font-semibold",
            className
          )}
          {...props}
        />

        <label
          className={clsx(
            "absolute left-3.5 pointer-events-none transition-all duration-150",
            isFocused || !!value
              ? "top-2 text-[11px] font-medium leading-none text-ink-3"
              : "top-1/2 -translate-y-1/2 text-sm text-ink-4"
          )}
        >
          {otpMode ? "Enter OTP" : label}
        </label>
      </div>

      {type === "password" && (
        <button
          type="button"
          onClick={() => setShowPassword((prev) => !prev)}
          aria-label={showPassword ? "Hide password" : "Show password"}
          className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-sm text-ink-4 transition-colors hover:bg-surface-hover hover:text-ink-2"
        >
          {showPassword ? <FiEyeOff size={17} /> : <FiEye size={17} />}
        </button>
      )}

      {/* Email verification buttons */}
      {type === "email" && !otpMode && !verified && validateEmailFormat(String(value)) && (
        <button
          type="button"
          onClick={handleVerifyClick}
          disabled={verifyLoading}
          className="absolute right-2 top-1/2 -translate-y-1/2 zs-btn zs-btn-soft zs-btn-sm"
        >
          {verifyLoading ? 'Sending...' : 'Verify'}
        </button>
      )}

      {type === "email" && verified && (
        <div className="flex items-center gap-2 absolute right-3 top-1/2 -translate-y-1/2">
          <span className="zs-badge zs-badge-success">
            <FiCheck aria-hidden="true" />
            Verified
          </span>
        </div>
      )}

      {type === "email" && otpMode && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-sm flex items-center gap-2">
          {String(value).length < 6 && (
            countdown > 0 ? (
              <span className="pr-1.5 text-xs font-medium tabular-nums text-ink-4">{countdown}s</span>
            ) : (
              <button type="button" onClick={handleResendOtp} className="zs-btn zs-btn-ghost zs-btn-sm text-primary-text">
                Resend OTP
              </button>
            )
          )}
          {String(value).length >= 6 && (
            <button type="button" disabled={otpVarifyLoading} onClick={handleVerifyOtp} className="zs-btn zs-btn-primary zs-btn-sm">
              {otpVarifyLoading ? 'Verifying...' : 'Verify OTP'}
            </button>
          )}
        </div>
      )}

      {type === "email" && otpMode && String(value).length == 0 && (
        <div className="absolute right-0 -top-6 text-xs">
          <button
            type="button"
            className="inline-flex items-center gap-1 zs-link"
            onClick={() => {
              setOtpMode(false);
              setInternalValue(emailValue);
            }}
          >
            <FiArrowLeft aria-hidden="true" />
            Change email
          </button>
        </div>
      )}
    </div>
  );
};
